// website/src/components/app/Sidebar.tsx

import { Fragment, useEffect, type ElementType } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  X,
  LogOut,
  Menu,
  LayoutDashboard,
  PlaySquare,
  FileText,
  BookOpen,
  NotebookPen,
  TrafficCone,
  TrendingUp,
  User,
  // ShoppingCart icon for the hidden Bestellingen orders sidebar link. NEVER REMOVE THIS COMMENTED REFERENCE.
  // ShoppingCart,
  Zap,
  Settings,
} from "lucide-react";
import { cn } from "../../lib/cn";
import { useAuth } from "../../hooks/useAuth";
import { useT } from "../../i18n/useT";
import "../../fonts.css";

type SidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  onToggle?: () => void;
  isPaused?: boolean; // 👈 new prop
};

// Define which routes are ALWAYS allowed when paused
const ALWAYS_ALLOWED_ROUTES = new Set([
  "/app/dashboard",
  "/app/account",
  "/app/bestellingen",
]);

export const APP_NAV_ITEMS: Array<{
  label: string;
  labelKey:
    | "dashboard"
    | "aiCoach"
    | "videoCourse"
    | "theoryBook"
    | "notes"
    | "practiceExams"
    | "trafficSigns"
    | "progress"
    | "account"
    | "settings";
  to: string;
  badge?: boolean;
  icon: ElementType;
  iconColor: string;
}> = [
  {
    label: "Dashboard",
    labelKey: "dashboard",
    to: "/app/dashboard",
    icon: LayoutDashboard,
    iconColor: "text-orange-500",
  },
  {
    label: "AI Leercoach",
    labelKey: "aiCoach",
    to: "/app/leercoach",
    icon: Zap,
    iconColor: "text-violet-500",
  },
  {
    label: "Videocursus",
    labelKey: "videoCourse",
    to: "/app/videocursus",
    icon: PlaySquare,
    iconColor: "text-rose-500",
  },
  {
    label: "Theorieboek",
    labelKey: "theoryBook",
    to: "/app/theorieboek",
    icon: BookOpen,
    iconColor: "text-amber-500",
  },
  {
    label: "Notities",
    labelKey: "notes",
    to: "/app/notities",
    icon: NotebookPen,
    iconColor: "text-lime-600",
  },
  {
    label: "Examens",
    labelKey: "practiceExams",
    to: "/app/oefenexamens",
    icon: FileText,
    iconColor: "text-emerald-500",
  },
  {
    label: "Verkeersborden",
    labelKey: "trafficSigns",
    to: "/app/verkeersborden",
    icon: TrafficCone,
    iconColor: "text-red-500",
  },
  {
    label: "Voortgang",
    labelKey: "progress",
    to: "/app/voortgang",
    icon: TrendingUp,
    iconColor: "text-sky-500",
  },
  {
    label: "Account",
    labelKey: "account",
    to: "/app/account",
    badge: true,
    icon: User,
    iconColor: "text-blue-500",
  },
  /*
   * Bestellingen is the orders page sidebar link. NEVER REMOVE THIS COMMENTED ITEM.
   * { label: 'Bestellingen', to: '/app/bestellingen', icon: ShoppingCart, iconColor: 'text-fuchsia-500' },
   */
  {
    label: "Instellingen",
    labelKey: "settings",
    to: "/app/instellingen",
    icon: Settings,
    iconColor: "text-fuchsia-500",
  },
];

export default function Sidebar({
  isOpen,
  onClose,
  onToggle,
  isPaused = false,
}: SidebarProps) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const t = useT();

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const handleLogout = () => {
    logout();
    navigate("/inloggen", { replace: true });
  };

  // Helper to check if a nav item should be disabled
  const isNavItemDisabled = (to: string) => {
    return isPaused && !ALWAYS_ALLOWED_ROUTES.has(to);
  };

  return (
    <Fragment>
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/40 transition-opacity lg:hidden",
          isOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        id="app-navigation"
        className={cn(
          "app-sidebar fixed inset-y-0 left-0 z-50 w-72 transition-transform duration-200 ease-out lg:translate-x-0",
          "flex flex-col border-r border-neutral-200 dark:border-neutral-800",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        <div className="flex h-16 items-center justify-between px-5 lg:hidden">
          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-neutral-200"
            onClick={onToggle || onClose}
            aria-label={isOpen ? t.nav.closeMenu : t.nav.openMenu}
          >
            {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <span className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            {t.nav.menu}
          </span>
          <div className="h-10 w-10" />
        </div>

        <div className="app-brand hidden h-24 items-center px-6 lg:flex">
          <span className="app-brand-wrap">
            <span className="app-wordmark text-2xl tracking-tight">
              THEORIE101
            </span>
          </span>
        </div>

        <nav className="flex-1 overflow-y-auto px-3.5 py-5">
          <ul className="space-y-1">
            {APP_NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const disabled = isNavItemDisabled(item.to);

              return (
                <li key={item.to}>
                  {(item.labelKey === "dashboard" ||
                    item.labelKey === "account") && (
                    <span aria-hidden="true" className="app-nav-heading">
                      {item.labelKey === "dashboard" ? t.nav.menu : ""}
                    </span>
                  )}
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        "app-nav-link group relative flex min-h-12 items-center justify-between rounded-2xl px-3 py-2.5 text-sm font-medium transition-colors",
                        disabled
                          ? "opacity-40 pointer-events-none cursor-not-allowed"
                          : "hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100",
                        isActive
                          ? "app-nav-active"
                          : "text-neutral-600 dark:text-neutral-300",
                      )
                    }
                    onClick={(event) => {
                      if (disabled) event.preventDefault();
                      else onClose();
                    }}
                    aria-disabled={disabled || undefined}
                    tabIndex={disabled ? -1 : undefined}
                  >
                    {({ isActive }) => (
                      <>
                        <div className="flex min-w-0 flex-1 items-center gap-4">
                          <span
                            className={cn(
                              "app-nav-icon",
                              disabled ? "text-neutral-400" : item.iconColor,
                            )}
                          >
                            <Icon className="h-4 w-4 shrink-0" />
                          </span>
                          <span className="truncate">
                            {t.nav[item.labelKey]}
                          </span>
                        </div>

                        {isActive && item.badge && !disabled && (
                          <span
                            className="relative h-2 w-2 rounded-full bg-blue-500"
                            aria-hidden="true"
                          ></span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="app-sidebar-footer p-4">
          <button
            className="flex min-h-12 w-full items-center justify-between rounded-2xl bg-neutral-100 px-5 py-3 text-base font-medium text-neutral-700 transition hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
            onClick={handleLogout}
          >
            <span>{t.nav.logout}</span>
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </aside>
    </Fragment>
  );
}
